import { useEffect, useState } from "react";
import {
  collection,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import { styles } from "../styles";
import { displayName, hasCapability } from "../utils/capabilities";
import JobCosting from "./JobCosting";
import JobServiceTickets from "./JobServiceTickets";
import BackButton from "../components/BackButton";

const STATUS_LABEL = {
  unassigned: "Unassigned",
  assigned: "Assigned",
  in_progress: "In Progress",
  closed: "Completed",
  canceled: "Canceled",
};

export default function JobDetailPage({
  job,
  customers,
  jobsRaw = [],
  serviceTicketsRaw = [],
  purchaseOrdersRaw = [],
  errorMessage,
  actions,
  role,
  capabilities,
  user,
  profile,
  orgId,
  logoUrl,
  orgName,
  orgAddress = {},
  quoteDefaults,
  maintenanceDefaults,
  contractsRaw = [],
  onBack,
}) {
  const canEditJobs = hasCapability(role, capabilities, "editJobs");
  // Estimated Bill (JobCosting) gated separately — splits editJobs into
  // job-ops vs billing visibility per Justin 2026-06-15. Most commercial
  // mechanical shops keep this off for techs (rates / margins / costs
  // shouldn't leak to field staff); light commercial + residential
  // shops can grant it per-user when techs double as estimators.
  // Manager tier gets it default-on via role; tech tier default-off.
  const canViewEstimatedBill = hasCapability(
    role,
    capabilities,
    "viewEstimatedBill"
  );
  const canManage = role === "admin" || role === "manager";

  // The calling user is "on" this job if they're one of the assigned techs.
  const isAssignedToMe = (job.assignedTechs || []).includes(user?.uid);

  // Org users — for the assignment picker. Managers/admins can read these
  // (firestore.rules), technicians can't, so only fetch when we can edit.
  const [orgUsers, setOrgUsers] = useState([]);
  const [usersError, setUsersError] = useState("");

  useEffect(() => {
    if (!orgId || !canEditJobs) return;
    const q = query(collection(db, "users"), where("orgId", "==", orgId));
    const unsub = onSnapshot(
      q,
      (snap) => setOrgUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => {
        console.error("org users listener error:", err);
        setUsersError(err.message);
      }
    );
    return unsub;
  }, [orgId, canEditJobs]);

  const customer = customers.find((c) => c.id === job.customerId);
  const location = customer?.locations.find((l) => l.id === job.locationId);
  const equipmentList = (location?.equipment || []).filter((eq) =>
    (job.equipmentIds || []).includes(eq.id)
  );

  // --- Status workflow -----------------------------------------------------
  // An assigned tech may only flip status (start / complete). Admin/manager
  // (editJobs) can also reopen and reassign.
  const startJob = () =>
    actions.updateJob(job.id, {
      status: "in_progress",
      updatedAt: new Date().toISOString(),
    });

  // Resolve the parent contract + the effective bill-automation mode
  // for maintenance visits. Per-contract override wins; falls back to
  // org default (which itself defaults to "off" when unset).
  const parentContract = job.contractId
    ? contractsRaw.find((c) => c.id === job.contractId)
    : null;
  const orgAutoDefault = maintenanceDefaults?.billAutomation || "off";
  const contractOverride = parentContract?.billAutomation;
  const effectiveAuto =
    contractOverride === "off" || contractOverride === "auto-draft"
      ? contractOverride
      : orgAutoDefault;
  const isPerVisitMaintenance =
    !!job.isMaintenanceVisit && parentContract?.billingMode === "per-visit";
  const shouldAutoDraft =
    isPerVisitMaintenance && effectiveAuto === "auto-draft";
  // Don't re-add a line if one was already auto-drafted (e.g. on
  // reopen + re-complete). Cheap check by tag.
  const alreadyAutoDrafted = (job.costing?.charges || []).some(
    (c) => c.autoDrafted
  );

  const completeJob = async () => {
    const willAutoDraft = shouldAutoDraft && !alreadyAutoDrafted;
    const prompt = isPerVisitMaintenance
      ? willAutoDraft
        ? "Mark this maintenance visit complete? A draft bill line will be added at the contracted per-visit price for the office to review."
        : "Mark this maintenance visit complete?"
      : "Mark this job complete?";
    if (!window.confirm(prompt)) return;

    if (willAutoDraft) {
      await actions.completeMaintenanceVisit(job.id, {
        autoDraft: true,
        contract: parentContract,
        visitJob: job,
      });
    } else {
      await actions.updateJob(job.id, {
        status: "closed",
        updatedAt: new Date().toISOString(),
        closedAt: new Date().toISOString(),
        closedBy: user?.email || "unknown",
      });
    }

    window.alert(
      willAutoDraft
        ? "Maintenance visit closed. A draft bill line was added for office review."
        : "Job marked complete. It moves to the Completed section."
    );
  };

  const reopenJob = () =>
    actions.updateJob(job.id, {
      status: (job.assignedTechs || []).length > 0 ? "assigned" : "unassigned",
      updatedAt: new Date().toISOString(),
      closedAt: null,
      closedBy: null,
      billFinalized: false,
      billFinalizedAt: null,
      billFinalizedBy: null,
    });

  // Scheduled date / estimated parts arrival live on the job doc (editJobs
  // only — keeps within the rules' full-update path).
  const updateSchedule = (field, value) =>
    actions.updateJob(job.id, {
      [field]: value,
      updatedAt: new Date().toISOString(),
    });

  // --- Assignment ----------------------------------------------------------
  const toggleTech = (uid) => {
    const current = job.assignedTechs || [];
    const next = current.includes(uid)
      ? current.filter((id) => id !== uid)
      : [...current, uid];
    // Auto-advance status off "unassigned" when the first tech is added,
    // and back to "unassigned" if the last tech is removed (only while the
    // job hasn't been started yet).
    const patch = {
      assignedTechs: next,
      updatedAt: new Date().toISOString(),
    };
    if (job.status === "unassigned" && next.length > 0) {
      patch.status = "assigned";
    } else if (job.status === "assigned" && next.length === 0) {
      patch.status = "unassigned";
    }
    actions.updateJob(job.id, patch);
  };

  const canStart =
    job.status === "assigned" && (isAssignedToMe || canEditJobs);
  const canComplete =
    job.status === "in_progress" && (isAssignedToMe || canEditJobs);
  const canReopen =
    (job.status === "closed" || job.status === "canceled") && canEditJobs;

  return (
    <>
      <BackButton onClick={onBack} />

      <h2 style={{ marginBottom: "4px" }}>
        {job.jobNumber ? `${job.jobNumber} — ` : ""}
        {job.title}
      </h2>
      <div style={{ fontSize: "13px", color: "#666", marginBottom: "12px" }}>
        Status: <strong>{STATUS_LABEL[job.status] || job.status}</strong>
        {job.jobType ? ` • ${job.jobType}` : ""}
        {job.quoteNumber ? ` • From quote ${job.quoteNumber}` : ""}
      </div>

      <div style={styles.card}>
        <strong>Job Info</strong>
        <p style={{ fontSize: "13px", margin: "6px 0" }}>
          <strong>Customer:</strong> {customer?.name || "—"}
          <br />
          <strong>Location:</strong> {location?.name || "—"}
        </p>
        {job.description && (() => {
          // Multi-line descriptions (typical for maintenance visits inheriting
          // a contract's scope-of-work bullet list) render as a bulleted <ul>.
          // Single-line descriptions stay inline (preserves the look of T&M
          // jobs that just have a one-sentence summary).
          const lines = job.description
            .split(/\r?\n/)
            .map((s) => s.trim())
            .filter(Boolean);
          const label = job.isMaintenanceVisit
            ? "Scope of work"
            : "Description";
          if (lines.length > 1) {
            return (
              <div style={{ fontSize: "13px", margin: "6px 0" }}>
                <strong>{label}:</strong>
                <ul style={{ margin: "4px 0", paddingLeft: "20px" }}>
                  {lines.map((line, i) => (
                    <li key={i}>{line}</li>
                  ))}
                </ul>
              </div>
            );
          }
          return (
            <p style={{ fontSize: "13px", margin: "6px 0" }}>
              <strong>{label}:</strong> {job.description}
            </p>
          );
        })()}
        <div style={{ fontSize: "13px", marginTop: "6px" }}>
          <strong>Equipment ({equipmentList.length}):</strong>
          {equipmentList.length === 0 ? (
            <span style={{ color: "#666" }}> none</span>
          ) : (
            <ul style={{ margin: "4px 0", paddingLeft: "20px" }}>
              {equipmentList.map((eq) => (
                <li key={eq.id}>
                  {eq.type}
                  {eq.subType ? ` (${eq.subType})` : ""}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div style={{ fontSize: "13px", marginTop: "8px" }}>
          {canEditJobs ? (
            <>
              <label>Scheduled date: </label>
              <input
                type="date"
                value={job.scheduledDate || ""}
                onChange={(e) => updateSchedule("scheduledDate", e.target.value)}
              />
              <br />
              <label>Estimated parts arrival: </label>
              <input
                type="date"
                value={job.estimatedPartsArrival || ""}
                onChange={(e) =>
                  updateSchedule("estimatedPartsArrival", e.target.value)
                }
              />
            </>
          ) : (
            <>
              <div>
                <strong>Scheduled date:</strong> {job.scheduledDate || "—"}
              </div>
              <div>
                <strong>Estimated parts arrival:</strong>{" "}
                {job.estimatedPartsArrival || "—"}
              </div>
            </>
          )}
        </div>
        {job.updateUponDiagnostic && (
          <p
            style={{
              fontSize: "12px",
              color: "#a15c00",
              margin: "6px 0 0",
              fontWeight: "bold",
            }}
          >
            Update upon Diagnostic — confirm equipment/scope after the on-site
            visit.
          </p>
        )}
      </div>

      {/* Assignment — admin/manager only. Lives BEFORE the Workflow card
          so the natural flow is: pick tech → press Start Job → tickets
          unlock. Start Job stays disabled (canStart=false) until a tech
          is on assignedTechs (status auto-flips to "assigned"). */}
      {canEditJobs && (
        <div style={styles.card}>
          <strong>Assigned Technicians</strong>
          {usersError && (
            <p style={{ color: "red", fontSize: "12px" }}>{usersError}</p>
          )}
          {orgUsers.filter(
            (u) => !u.deleted && u.fieldEligible !== false
          ).length === 0 ? (
            <p style={{ fontSize: "12px", color: "#666" }}>
              No team members to assign. (Office staff and any user flagged
              not field-eligible on the Users page won't show here — toggle
              them on in ManageUsersPage if you need someone added.)
            </p>
          ) : (
            orgUsers
              .filter((u) => !u.deleted && u.fieldEligible !== false)
              .map((u) => (
                <label
                  key={u.id}
                  style={{ display: "block", fontSize: "13px", marginTop: "4px" }}
                >
                  <input
                    type="checkbox"
                    checked={(job.assignedTechs || []).includes(u.id)}
                    onChange={() => toggleTech(u.id)}
                  />{" "}
                  {displayName(u.profile, u.email)} ({u.role})
                </label>
              ))
          )}
        </div>
      )}

      {/* Status workflow — sits AFTER Assignment so the natural flow is
          assign-tech → Start Job → tickets. Start Job appears only when
          canStart (status === "assigned") which itself depends on at
          least one tech being on assignedTechs. */}
      {(canStart || canComplete || canReopen) && (
        <div style={styles.card}>
          <strong>Workflow</strong>
          <div style={{ marginTop: "8px" }}>
            {canStart && (
              <button style={styles.button} onClick={startJob}>
                Start Job
              </button>
            )}
            {canComplete && (
              <button style={styles.button} onClick={completeJob}>
                Mark Complete
              </button>
            )}
            {canReopen && (
              <button style={styles.button} onClick={reopenJob}>
                Reopen Job
              </button>
            )}
          </div>
        </div>
      )}

      {/* Service tickets — assigned techs + managers/admins can file, but
          ONLY while the job is IN PROGRESS (started). Pre-start states
          (unassigned / assigned / scheduled) and post-finalize states
          (closed / billFinalized) both lock tickets. Forces the flow:
          assign tech → Start Job → file tickets → Mark Complete → bill. */}
      <JobServiceTickets
        job={job}
        tickets={serviceTicketsRaw.filter((t) => t.jobId === job.id)}
        equipmentList={equipmentList}
        actions={actions}
        user={user}
        profile={profile}
        canFile={
          (isAssignedToMe || canEditJobs) &&
          job.status === "in_progress" &&
          !job.billFinalized
        }
        errorMessage={errorMessage}
        customer={customer}
        location={location}
        customerName={customer?.name}
        locationName={location?.name}
        jobsRaw={jobsRaw}
        canManage={canManage}
        role={role}
        capabilities={capabilities}
        logoUrl={logoUrl}
        orgName={orgName}
        orgAddress={orgAddress}
        jobPOs={purchaseOrdersRaw.filter((po) => po.jobId === job.id)}
        orgUsers={orgUsers}
      />

      {/* Estimated bill — gated by viewEstimatedBill (split from
          editJobs 2026-06-15 so techs can edit jobs without seeing
          rates / margins / costs). Manager tier default-on; tech tier
          default-off but grantable. */}
      {canViewEstimatedBill && (
        <JobCosting
          job={job}
          actions={actions}
          quoteDefaults={quoteDefaults}
          customer={customer}
          location={location}
          user={user}
          role={role}
          capabilities={capabilities}
          profile={profile}
          logoUrl={logoUrl}
          orgName={orgName}
          orgAddress={orgAddress}
        />
      )}
    </>
  );
}
